import { defineStore } from 'pinia';
import { computed } from 'vue';
import { useSessionStorage } from '@vueuse/core';

export const useProfile = defineStore('profile', () => {
    const emptyProfileFactory = () => ({
        hasUser: false,
        email: undefined as string | undefined,
        countryCode: undefined as number | undefined,
        nationalNumber: undefined as string | undefined,
        firstname: undefined as string | undefined,
        lastname: undefined as string | undefined,
        profileImageUrl: undefined as string | undefined,
        salutation: undefined as string | undefined,
        flags: {
            exists: false,
            isMiniProfile: false,
        },
    });

    // Store the entire state in a single object persisted in sessionStorage
    const state = useSessionStorage('lokarb:profile-store', emptyProfileFactory());

    const hasProfile = computed(() => Boolean(state.value.flags.exists));
    const hasValidProfile = computed(() => Boolean(state.value.firstname));
    const hasMiniProfile = computed(() => Boolean(state.value.flags.isMiniProfile));

    const hasUser = computed(() => state.value.hasUser);
    const email = computed(() => state.value.email);
    const countryCode = computed(() => state.value.countryCode);
    const nationalNumber = computed(() => state.value.nationalNumber);
    const firstname = computed(() => state.value.firstname);
    const lastname = computed(() => state.value.lastname);
    const profileImageUrl = computed(() => state.value.profileImageUrl);
    const salutation = computed(() => state.value.salutation);

    // Getters
    const profileFetched = computed(() => state.value.email !== undefined);

    const userTypeDetail = computed(() => {
        if (!state.value.hasUser) return null;
        if (hasProfile.value) return 'profile';
        if (hasMiniProfile.value) return 'mini-profile';
        return 'account';
    });

    // Actions
    function setProfile(profile: object) {
        state.value.hasUser = true;
        state.value.email = profile.basics.contact.email;
        state.value.countryCode = profile.basics.contact.mobile?.countryCode;
        state.value.nationalNumber = profile.basics.contact.mobile?.nationalNumber;
        state.value.firstname = profile.basics.firstname;
        state.value.lastname = profile.basics.surname;
        state.value.salutation = profile.basics.salutation;
        state.value.flags = profile.flags;

        const imageType = 'square';
        state.value.profileImageUrl =
            profile.image.length > 0 ?
                profile.image.find((result: object) => result.version === imageType)?.url :
                null;
    }

    function $reset() {
        state.value = emptyProfileFactory();
    }

    return {
        state,
        // Getters
        profileFetched,
        userTypeDetail,
        hasProfile,
        hasValidProfile,
        hasMiniProfile,
        hasUser,
        email,
        firstname,
        lastname,
        countryCode,
        nationalNumber,
        profileImageUrl,
        salutation,
        // Actions
        setProfile,
        $reset,
    };
});
